// /src/js/theme.js

import { gsap } from "gsap";

// Mark that JS loaded (debug only), but do NOT hide OS cursor yet.
document.documentElement.classList.add("js");

/* Prevent native HTML drag from stealing pointermove in .drag zones */
document.addEventListener(
	"dragstart",
	(e) => {
		if (e.target && e.target.closest(".drag")) e.preventDefault();
	},
	true
);

// Build/ensure the cursor element
(function initCursor() {
	const isCoarse = window.matchMedia("(pointer: coarse)").matches;
	if (isCoarse) return;

	let cursor = document.querySelector(".custom-cursor");
	if (!cursor) {
		cursor = document.createElement("div");
		cursor.className = "custom-cursor";
		cursor.setAttribute("aria-hidden", "true");
		document.body.appendChild(cursor);
	}

	let cursorOverlay = document.querySelector(".custom-cursor-overlay");
	if (!cursorOverlay) {
		cursorOverlay = document.createElement("div");
		cursorOverlay.className = "custom-cursor-overlay";
		cursorOverlay.setAttribute("aria-hidden", "true");
		document.body.appendChild(cursorOverlay);
	}

	cursor.style.zIndex = "2147483646";
	cursorOverlay.style.zIndex = "2147483647";

	const moveX = gsap.quickTo(cursor, "x", {
		duration: 0.12,
		ease: "power2.out",
	});
	const moveY = gsap.quickTo(cursor, "y", {
		duration: 0.12,
		ease: "power2.out",
	});

	// Also move the overlay
	const moveXOverlay = gsap.quickTo(cursorOverlay, "x", {
		duration: 0.12,
		ease: "power2.out",
	});
	const moveYOverlay = gsap.quickTo(cursorOverlay, "y", {
		duration: 0.12,
		ease: "power2.out",
	});

	const grow = () => {
		cursor.classList.add("is-hover");
		cursorOverlay.classList.add("is-hover");
	};
	const shrink = () => {
		cursor.classList.remove("is-hover");
		cursorOverlay.classList.remove("is-hover");
	};

	let revealed = false;
	const onMove = (e) => {
		moveX(e.clientX);
		moveY(e.clientY);
		moveXOverlay(e.clientX);
		moveYOverlay(e.clientY);

		if (!revealed) {
			gsap.to(cursor, { opacity: 1, duration: 0.25 });
			gsap.to(cursorOverlay, { opacity: 1, duration: 0.25 });
			document.documentElement.classList.add("cursor-ready");
			revealed = true;
		}
	};

	document.addEventListener("pointermove", onMove, {
		passive: true,
		capture: true,
	});
	if (document.addEventListener) {
		document.addEventListener("pointerrawupdate", onMove, {
			passive: true,
			capture: true,
		});
	}

	window.addEventListener("mouseout", () => {
		gsap.to(cursor, { opacity: 0, duration: 0.25 });
		gsap.to(cursorOverlay, { opacity: 0, duration: 0.25 });
		revealed = false;
		document.documentElement.classList.remove("cursor-ready");
	});

	// Hover grow
	const INTERACTIVE_SELECTOR = [
		"a[href]",
		"button",
		"[role='button']",
		"input[type='submit']",
		"input[type='button']",
		"input[type='reset']",
	].join(",");

	document.addEventListener(
		"pointerover",
		(e) => {
			const el = e.target.closest?.(INTERACTIVE_SELECTOR);
			if (!el || el.disabled || el.classList.contains("cursor-ignore"))
				return;
			// entering an interactive element → grow
			grow();
		},
		true
	);

	document.addEventListener(
		"pointerout",
		(e) => {
			const from = e.target.closest?.(INTERACTIVE_SELECTOR);
			if (!from) return;

			const to = e.relatedTarget?.closest?.(INTERACTIVE_SELECTOR);

			if (to === from) return;

			shrink();
		},
		true
	);
})();

// -- Drag-mode: detect when the pointer is over a .drag region
(function wireCursorMode() {
	const root = document.body;
	if (!root) return;

	let mx = 0,
		my = 0;
	document.addEventListener(
		"pointermove",
		(e) => {
			mx = e.clientX;
			my = e.clientY;
		},
		{ passive: true }
	);

	function getCursorMode(x, y, doc = document) {
		const el = doc.elementFromPoint(x, y);
		if (!el) return null;

		const isInteractive = !!el.closest(
			"a[href], button, [role='button'], input[type='submit'], input[type='button']"
		);

		// Allow blocks to opt-in to a "solid" cursor mode (even when hovering links/buttons inside them)
		const isSolidZone = !!el.closest(".cursor-solid, [data-cursor-mode='solid']");

		// Check for overlay drag first (more specific)
		// Note: keep drag modes from overriding interactive elements
		if (!isInteractive) {
			if (
				el.closest(".drag-overlay") ||
				el.closest("[data-cursor-mode='drag-overlay']")
			) {
				return "drag-overlay";
			}
		}

		// Then regular drag
		if (!isInteractive) {
			if (el.closest(".drag") || el.closest("[data-cursor-mode='drag']")) {
				return "drag";
			}
		}

		if (isSolidZone) return "solid";

		return null;
	}

	function tick() {
		const mode = getCursorMode(mx, my);
		if (mode) {
			root.setAttribute("data-cursor", mode);
		} else {
			root.removeAttribute("data-cursor");
		}
		requestAnimationFrame(tick);
	}

	requestAnimationFrame(tick);
})();

document.addEventListener("DOMContentLoaded", () => {
	const header = document.querySelector("header.site-header");
	const body = document.body;
	const expandedMenu = document.querySelector(".expanded-menu");
	const openBtn = document.querySelector(".navbar-toggler");
	const closeBtn = document.querySelector(".ex-toggler");

	/* -------------------------
	   Mobile menu open/close
	-------------------------- */

	function openMenu() {
		if (!expandedMenu) return;
		body.classList.add("is--open");

		// show expanded menu (CSS will handle animation)
		expandedMenu.classList.add("expanded-menu--open");

		// swap buttons
		if (openBtn) openBtn.classList.add("is-hidden");
		if (closeBtn) closeBtn.classList.add("is-visible");

		// make sure header is visible while menu is open
		if (header) header.classList.remove("header--hidden");
	}

	function closeMenu() {
		if (!expandedMenu) return;
		body.classList.remove("is--open");
		expandedMenu.classList.remove("expanded-menu--open");

		if (openBtn) openBtn.classList.remove("is-hidden");
		if (closeBtn) closeBtn.classList.remove("is-visible");
	}

	if (openBtn) {
		openBtn.addEventListener("click", (e) => {
			e.preventDefault();
			if (body.classList.contains("is--open")) {
				closeMenu();
			} else {
				openMenu();
			}
		});
	}

	if (closeBtn) {
		closeBtn.addEventListener("click", (e) => {
			e.preventDefault();
			closeMenu();
		});
	}

	// Close menu when clicking any link inside the expanded menu
	if (expandedMenu) {
		expandedMenu.addEventListener("click", (e) => {
			const link = e.target.closest("a");
			if (link) {
				closeMenu();
			}
		});
	}

	/* -------------------------
	   Desktop nav dropdowns
	-------------------------- */
	(function initNavDropdowns() {
		const nav = document.querySelector("#navbar-menu #menu-main-menu");
		if (!nav) return;

		const parents = nav.querySelectorAll(":scope > li.menu-item-has-children");
		const closeAll = (except) => {
			parents.forEach((li) => {
				if (li !== except) {
					li.classList.remove("is-open");
					const trigger = li.querySelector(":scope > a, :scope > .menu-parent-item");
					if (trigger) trigger.setAttribute("aria-expanded", "false");
				}
			});
		};

		parents.forEach((li) => {
			const trigger = li.querySelector(":scope > a, :scope > .menu-parent-item");
			if (!trigger) return;
			trigger.setAttribute("aria-haspopup", "true");
			trigger.setAttribute("aria-expanded", "false");

			// Touch / click toggle (hover still works via CSS)
			trigger.addEventListener("click", (e) => {
				const isTouch =
					window.matchMedia("(hover: none), (pointer: coarse)").matches;
				if (!isTouch) return; // let link navigate on desktop
				e.preventDefault();
				const open = !li.classList.contains("is-open");
				closeAll(li);
				li.classList.toggle("is-open", open);
				trigger.setAttribute("aria-expanded", open ? "true" : "false");
			});
		});

		document.addEventListener("click", (e) => {
			if (!nav.contains(e.target)) closeAll();
		});

		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape") closeAll();
		});
	})();

	/* -------------------------
	   Header hide/show on scroll
	-------------------------- */

	if (!header) return;

	let lastY = window.pageYOffset;
	const THRESHOLD = 8; // px to avoid micro-jitters

	window.addEventListener(
		"scroll",
		() => {
			const currentY = window.pageYOffset;
			const diff = currentY - lastY;

			// If menu is open, never hide header
			if (document.body.classList.contains("is--open")) {
				header.classList.remove("header--hidden");
				lastY = currentY;
				return;
			}

			// Always show when near top
			if (currentY < 80) {
				header.classList.remove("header--hidden");
				lastY = currentY;
				return;
			}

			// Ignore tiny movements
			if (Math.abs(diff) < THRESHOLD) {
				return;
			}

			if (diff > 0) {
				// scrolling down
				header.classList.add("header--hidden");
			} else {
				// scrolling up
				header.classList.remove("header--hidden");
			}

			lastY = currentY;
		},
		{ passive: true }
	);
});
